import React from 'react'

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@digital-twins/design-system/components/ui/tooltip'

/**
 * One 100% stacked bar, shared by categorical-share and diverging-likert.
 *
 * The component is the bar node itself (the slide's `row-bars` div). Callers
 * pass `rootStyle` for the margin or flex the host already used. Segment
 * markup and inline style order match the stacked bar that the static goldens
 * captured, so a slide row stays byte-identical.
 */

export interface SegmentBarSegment {
  key: string
  /** Printed (stamped) share, 0-100: decides whether the label is drawn. */
  share: number
  /**
   * The share the segment is drawn with, 0-100: Python's unrounded `exactShare`,
   * so a row's segments fill the bar. A zero share stays in the row at width 0.
   */
  exactShare: number
  fill: string
  /** Label colour, chosen by the caller. */
  text: string
  /** Already-formatted label ("54%", "54% · 27"). */
  printed: string
  title: string
  testId: string
  /** Default 0. Stacked rows pass 12, or 2 for an opened Other. */
  minWidthPx?: number
  dataAttrs?: Record<string, string>
}

export interface SegmentBarProps {
  segments: Array<SegmentBarSegment>
  heightPx: number
  labelMinSharePct: number
  radiusPx?: number
  ariaLabel?: string
  testId?: string
  /** Merged onto the bar div. `marginTop` is placed first so slide goldens keep their style order. */
  rootStyle?: React.CSSProperties
  /**
   * `native` is the browser tooltip (`title`), and the slide default: the
   * captured markup stays the bar div. `fast` is the survey tab: the same
   * tooltip the chart labels use, focusable, 150ms.
   */
  hover?: 'native' | 'fast'
}

/** The stacked label rule, keyed on the category list, never on how many pieces one row draws. */
export function defaultLabelMinSharePct(categoryCount: number): number {
  if (categoryCount > 4) {
    return 14
  }
  return 7
}

function segmentStyle(segment: SegmentBarSegment): React.CSSProperties {
  return {
    width: `${Math.max(0, segment.exactShare)}%`,
    minWidth: segment.minWidthPx ?? 0,
    flexShrink: 0,
    height: '100%',
    background: segment.fill,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  }
}

interface SegmentFaceProps {
  segment: SegmentBarSegment
  labelMinSharePct: number
  fast: boolean
}

function SegmentFace(props: SegmentFaceProps) {
  const { segment, labelMinSharePct, fast } = props
  const label = segment.share >= labelMinSharePct ? (
    <span style={{ fontSize: 11, fontWeight: 500, lineHeight: 1.2, whiteSpace: 'nowrap', color: segment.text }}>{segment.printed}</span>
  ) : null
  const face = (
    <div
      data-testid={segment.testId}
      data-top={segment.dataAttrs?.['data-top']}
      title={fast ? undefined : segment.title}
      tabIndex={fast ? 0 : undefined}
      aria-label={fast ? segment.title : undefined}
      style={segmentStyle(segment)}
    >
      {label}
    </div>
  )
  if (!fast) {
    return face
  }
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>{face}</TooltipTrigger>
      <TooltipContent>{segment.title}</TooltipContent>
    </Tooltip>
  )
}

export function SegmentBar(props: SegmentBarProps) {
  const { segments, heightPx, labelMinSharePct, radiusPx = 2, ariaLabel, testId, rootStyle, hover = 'native' } = props
  const marginTop = rootStyle?.marginTop
  const rest: React.CSSProperties = { ...(rootStyle ?? {}) }
  delete rest.marginTop
  const fast = hover === 'fast'

  const bar = (
    <div
      data-testid={testId}
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
      style={{
        marginTop,
        height: heightPx,
        display: 'flex',
        overflow: 'hidden',
        borderRadius: radiusPx,
        ...rest,
      }}
    >
      {segments.map((segment) => (
        <SegmentFace key={segment.key} segment={segment} labelMinSharePct={labelMinSharePct} fast={fast} />
      ))}
    </div>
  )
  if (!fast) {
    return bar
  }
  return <TooltipProvider>{bar}</TooltipProvider>
}
